import { useState, useRef } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Download, Play, Pause } from "lucide-react";
import { STORE } from "../data/content";
import { SectionLabel, Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Preview player — renders only when a preview clip URL is set.
function PreviewButton({ src, testid, size = "w-14 h-14" }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  if (!src) return null;
  const toggle = (e) => {
    e.stopPropagation();
    const a = ref.current;
    if (playing) a.pause();
    else a.play();
  };
  return (
    <>
      <audio ref={ref} src={src} preload="none" onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => { if (e.target.currentTime >= 30) { e.target.pause(); e.target.currentTime = 0; } }} />
      <button
        onClick={toggle}
        data-testid={testid}
        data-cursor
        className={`${size} rounded-full glass-strong flex items-center justify-center text-white hover:scale-105 transition-transform`}
        aria-label="Preview"
      >
        {playing ? <Pause size={18} className="fill-white" /> : <Play size={18} className="fill-white ml-0.5" />}
      </button>
    </>
  );
}

async function startCheckout(lookup_key, setBusy, id) {
  try {
    setBusy(id);
    const res = await fetch(`${API}/payments/checkout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ lookup_key, quantity: 1, origin_url: window.location.origin }),
    });
    const data = await res.json();
    if (data.checkout_url) {
      window.location.href = data.checkout_url;
    } else {
      toast.error("Could not start checkout. Try again.");
      setBusy(null);
    }
  } catch (e) {
    toast.error("Checkout unavailable right now.");
    setBusy(null);
  }
}

export default function OwnMusic() {
  const [busy, setBusy] = useState(null);

  return (
    <section id="own" className="relative w-full py-24 md:py-36 px-5 md:px-10 bg-[color:var(--graphite)]" data-testid="section-own">
      <div className="max-w-[1400px] mx-auto">
        <SectionLabel index="STORE" id="own-label">DIGITAL</SectionLabel>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <h2 className="font-display chrome-text text-5xl sm:text-6xl md:text-8xl leading-[0.9]">
            <Reveal text="OWN THE MUSIC" />
          </h2>
          <p className="font-body text-white/50 max-w-sm text-sm md:text-base">
            Buy the music directly. Secure checkout, instant digital ownership.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-6 md:gap-8">
          {/* Album feature */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-8% 0px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6 group relative overflow-hidden rounded-sm ring-1 ring-white/10 flex flex-col"
            data-testid="store-album"
          >
            <div className="relative overflow-hidden">
              <img src={STORE.album.cover} alt={STORE.album.name} className="w-full aspect-square object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <span className="absolute top-4 left-4 track-label text-white/70">ALBUM</span>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <PreviewButton src={STORE.album.preview} testid="preview-album" />
              </div>
            </div>
            <div className="p-6 md:p-8 flex items-center justify-between gap-4 bg-[color:var(--charcoal)]">
              <div>
                <h3 className="font-display steel-text text-2xl md:text-3xl leading-none">{STORE.album.name}</h3>
                <p className="font-body text-white/45 text-sm mt-2">{STORE.album.desc}</p>
              </div>
              <button
                onClick={() => startCheckout(STORE.album.lookup_key, setBusy, STORE.album.id)}
                disabled={busy === STORE.album.id}
                data-testid="buy-album"
                data-cursor
                className="shrink-0 flex items-center gap-2 px-6 py-4 bg-white text-black track-label hover:bg-white/80 transition-colors disabled:opacity-60"
              >
                {busy === STORE.album.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                {STORE.album.price}
              </button>
            </div>
          </motion.div>

          {/* Singles */}
          <div className="lg:col-span-6 grid sm:grid-cols-2 gap-6 md:gap-8">
            {STORE.singles.map((s, i) => (
              <motion.div
                key={s.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-8% 0px" }}
                transition={{ duration: 0.8, delay: 0.1 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="group relative overflow-hidden rounded-sm ring-1 ring-white/10 flex flex-col"
                data-testid={`store-single-${s.id}`}
              >
                <div className="relative overflow-hidden">
                  <img src={s.cover} alt={s.name} className="w-full aspect-square object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <span className="absolute top-3 left-3 track-label text-white/70">SINGLE</span>
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <PreviewButton src={s.preview} testid={`preview-${s.id}`} size="w-12 h-12" />
                  </div>
                </div>
                <div className="p-5 flex items-center justify-between gap-3 bg-[color:var(--charcoal)] flex-1">
                  <div>
                    <h3 className="font-display steel-text text-xl leading-none">{s.name}</h3>
                    {s.featured && <p className="track-label text-white/35 mt-2">{s.featured}</p>}
                  </div>
                  <button
                    onClick={() => startCheckout(s.lookup_key, setBusy, s.id)}
                    disabled={busy === s.id}
                    data-testid={`buy-single-${s.id}`}
                    data-cursor
                    className="shrink-0 flex items-center gap-2 px-4 py-2.5 border border-white/20 hover:border-white hover:bg-white hover:text-black transition-all duration-300 track-label disabled:opacity-60"
                  >
                    {busy === s.id ? <Loader2 size={14} className="animate-spin" /> : null}
                    {s.price}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
